'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AskPanel, SearchInput, SearchProvider, SearchResults,
  useFilterSuggestion, useSearchContext, useSearchOrAsk,
} from '@indxsearch/intrface';
import type { AskFilters, AskStatus } from '@indxsearch/intrface';
import { Base, Button, Composer, Kbd, StreamStatus, Suggestions } from '@indxsearch/systm';
import { Ai_agent, Compose, Indx, Sliders_horizontal } from '@indxsearch/pixl';
import { fields, filters, renderFilm } from './SearchDemo';
import demo from './SearchDemo.module.css';
import styles from './SearchAskDemo.module.css';

// The hero's movie search with Search or ask, worked out in IndxSolutions' labs/democenter
// (?client=movies&tabs=top-buttons) and kept in step with it. The films, their filters and the
// frame are SearchDemo's; what this adds is the assistant beside them:
//   - the field judges what is typed; a strong offer takes the results' place with a prompt to ask
//   - the side column switches between Filters and Assistant (a secondary and a ghost Button)
//   - the assistant's filters go on the list, which is shown once its search has settled
//   - the panel takes the results' height and scrolls inside it, the composer at its bottom
//   - on a narrow screen two buttons under the field open a panel in the results' place
//
// The server comes from NEXT_PUBLIC_HERO_URL, _TEAM, _DATASET and _TOKEN, falling back to the
// hero's own (NEXT_PUBLIC_INDX_*): the dataset needs Search or ask on, as a catalogue.
const HERO = {
  url: process.env.NEXT_PUBLIC_HERO_URL ?? process.env.NEXT_PUBLIC_INDX_URL ?? '',
  team: process.env.NEXT_PUBLIC_HERO_TEAM ?? 'indx',
  dataset: process.env.NEXT_PUBLIC_HERO_DATASET ?? process.env.NEXT_PUBLIC_INDX_DATASET ?? '',
  token: process.env.NEXT_PUBLIC_HERO_TOKEN ?? process.env.NEXT_PUBLIC_INDX_TOKEN ?? '',
};

/** Questions to start from, each one the catalogue answers well. */
const EXAMPLES = [
  'a funny space movie from the 80s',
  'Which film has Tom Hanks stranded on an island?',
  'an animated film the whole family will enjoy',
];

/** How long the list must stay unchanged after the assistant's filters before it is shown:
 *  longer than the facet search's debounce (facetDebounceDelayMillis) and its round trip. */
const SETTLE_MS = 450;

const renderResult = renderFilm({ description: true });

type Panel = 'filters' | 'assistant';
const PANELS: { value: Panel; label: string; icon: React.ComponentType<{ size?: number; color?: string }> }[] = [
  // No count: the search field's chip already says how many filters are on.
  { value: 'filters', label: 'Filters', icon: Sliders_horizontal },
  { value: 'assistant', label: 'Assistant', icon: Ai_agent },
];

interface Props {
  onSearchFocus?: () => void
  onSearchBlur?: () => void
  onQueryChange?: (query: string) => void
}

function Layout({ onSearchFocus, onSearchBlur, onQueryChange }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [narrow, setNarrow] = useState(false);
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<Panel>('filters');
  const [question, setQuestion] = useState<string | null>(null);
  const [run, setRun] = useState(0);
  const [askStatus, setAskStatus] = useState<AskStatus>('idle');
  // What the results area waits for after asking: the assistant's filters ('asking'), then the
  // page's own search with them ('applied', with the queryId the list had before).
  const [pending, setPending] = useState<{ phase: 'asking' } | { phase: 'applied'; before?: string } | null>(null);
  // Typing after asking hands the field back to the judge; the assistant's own search (it
  // rewrites the query) must not, which is why it is the keys that count and not the text.
  const [typedSinceAsk, setTypedSinceAsk] = useState(true);
  const [draft, setDraft] = useState('');
  const { state, setQuery, setSort, setSearchSettings } = useSearchContext();
  const sor = useSearchOrAsk();
  const suggestion = useFilterSuggestion({ auto: sor });

  // The hero's own order. The assistant can put another on ("sorted by votes"); Clear takes it off
  // again, with the text and the filters, while an order the visitor chose stays theirs.
  const restoreSort = useCallback(() => setSort('popularity', false), [setSort]);
  const [assistantSorted, setAssistantSorted] = useState(false);
  useEffect(() => {
    restoreSort();
    setSearchSettings({ placeholderText: 'Search for a movie, or ask', coverageDepth: 2000, showScore: false });
  }, [restoreSort, setSearchSettings]);
  useEffect(() => { onQueryChange?.(state.query); }, [state.query, onQueryChange]);

  // Under 800px the column gives way to two buttons under the field.
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => setNarrow(entry.contentRect.width <= 800));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  useEffect(() => { if (!narrow) setOpen(false); }, [narrow]);

  const ask = (text: string) => {
    const q = text?.trim();
    if (!q) return;
    setQuestion(q);
    setRun(r => r + 1);
    setAskStatus('idle');
    setTypedSinceAsk(false);
    setPending({ phase: 'asking' });
    setPanel('assistant');
    if (narrow) setOpen(true);
  };
  const askExample = (text: string) => { setQuery(text); ask(text); };
  // Asked from the assistant itself, the search field is left as it is.
  const askHere = (text: string) => { setDraft(''); ask(text); };
  const newConversation = () => { setQuestion(null); setRun(r => r + 1); };

  const onFilters = (f: AskFilters) => {
    suggestion.apply(f);
    if (f.sort) setAssistantSorted(true);
    setPending({ phase: 'applied', before: state.queryId });
  };
  // Settled, not merely landed: after the filters the page searches more than once (the fast
  // search, then the debounced facet search, which can bring a different list).
  useEffect(() => {
    if (!pending) return;
    if (pending.phase === 'applied' && !state.isLoading && state.queryId !== pending.before) {
      const t = setTimeout(() => setPending(null), SETTLE_MS);
      return () => clearTimeout(t);
    }
    // No filters came (a failure, a limit, or an answer that needed none): the answer is the end.
    if (pending.phase === 'asking' && ['answered', 'error', 'limited'].includes(askStatus)) setPending(null);
  }, [pending, state.isLoading, state.queryId, askStatus]);
  // Never wait for ever: a search that does not come back shows what there is.
  useEffect(() => {
    if (!pending) return;
    const t = setTimeout(() => setPending(null), pending.phase === 'applied' ? 4000 : 30000);
    return () => clearTimeout(t);
  }, [pending]);

  // The field's Clear button (aria-label "Clear search", or "... and filters"). Its click, not an
  // empty field: an assistant's answer can itself leave no text and no filters, only an order.
  const onHeaderClick = (e: React.MouseEvent) => {
    const button = (e.target as HTMLElement).closest('button');
    if (assistantSorted && button?.getAttribute('aria-label')?.startsWith('Clear search')) {
      restoreSort();
      setAssistantSorted(false);
    }
  };

  const text = state.query.trim();
  const asked = question != null && !typedSinceAsk;
  // While the offer stands for what is in the field (intrface): typing on keeps the prompt, a
  // backspace takes it away until the judge has read the new text.
  const strong = sor.offer === 'strong' && sor.stands;
  const main = asked ? (pending ? 'working' : 'results') : strong ? 'prompt' : 'results';

  const toggle = (value: Panel) => {
    if (open && panel === value) setOpen(false);
    else { setPanel(value); setOpen(true); }
  };

  // Both panels stay mounted: switching to the filters must not throw the conversation away. It
  // is rendered once, beside the results or (narrow) in their place.
  const side = (
    <div className={styles.side}>
      {!narrow && (
        <div className={styles.sideHead}>
          <div className={styles.switch} role="group" aria-label="Side panel">
            {PANELS.map(p => (
              <Button key={p.value} size="micro" variant={panel === p.value ? 'secondary' : 'ghost'} aria-pressed={panel === p.value}
                      iconLeft={<p.icon />} onClick={() => setPanel(p.value)}>{p.label}</Button>
            ))}
          </div>
          {/* The logo at the frame's far right, over the side column. */}
          <span className={demo.logo}><Indx size={28} color="var(--lv4)" /></span>
        </div>
      )}
      <div className={styles.panels}>
        <div className={styles.filters} hidden={panel !== 'filters'}>{filters}</div>
        <div className={styles.assistant} hidden={panel !== 'assistant'}>
          {question ? (
            <>
              <div className={styles.assistantHead}>
                <Button variant="ghost" size="micro" iconLeft={<Compose />} onClick={newConversation}>New conversation</Button>
              </div>
              <AskPanel key={run} question={question} followUps fill className={styles.askFill}
                        onFilters={onFilters} onStatusChange={setAskStatus} />
            </>
          ) : (
            <div className={styles.empty}>
              <p>Ask about a film, or describe what you would like to watch. The answer comes from this catalogue, with the films it used.</p>
              <Suggestions items={EXAMPLES} onPick={askExample} />
              <Composer value={draft} onChange={setDraft} onSend={askHere}
                        placeholder="Ask the assistant…" aria-label="Ask the assistant" />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={demo.wrapper} ref={ref}>
      <Base className={`${demo.component} ${narrow ? '' : styles.tall}`}>
        <div className={demo.mainContent}>
          <div className={demo.header} onClick={onHeaderClick} onKeyDown={e => { if (e.key !== 'Enter') setTypedSinceAsk(true); }}>
            <SearchInput showFocus={false} className={demo.searchInput} onFocus={onSearchFocus} onBlur={onSearchBlur}
                         onAsk={ask} searchOrAsk={sor} askOffer="none" />
            {narrow && <div className={demo.meta}><span className={demo.logo}><Indx size={28} color="var(--lv4)" /></span></div>}
          </div>
          {narrow && (
            <div className={styles.mobileBar}>
              {open && <Button variant="secondary" size="micro" className={styles.mobileBack} onClick={() => setOpen(false)}>Show results</Button>}
              {PANELS.map(p => (
                <Button key={p.value} size="micro" variant={open && panel === p.value ? 'secondary' : 'ghost'}
                        aria-pressed={open && panel === p.value} iconLeft={<p.icon />} onClick={() => toggle(p.value)}>{p.label}</Button>
              ))}
            </div>
          )}
          {narrow && open && <div className={styles.mobilePanel}>{side}</div>}
          <div className={demo.body} hidden={narrow && open}>
            {main === 'prompt' && (
              <div className={styles.prompt} aria-live="polite">
                <span className={styles.promptIcon}><Ai_agent size={20} color="currentColor" /></span>
                <div className={styles.promptText}>
                  <span className={styles.promptTitle}>This reads like a question</span>
                  <p>The assistant answers it from the catalogue, and puts the films it means on the list.</p>
                </div>
                <div className={styles.promptActions}>
                  <Button variant="primary" className={styles.askStrong} iconLeft={<Ai_agent />} iconRight={<Kbd>Enter ↵</Kbd>} onClick={() => ask(text)}>Ask the assistant</Button>
                </div>
              </div>
            )}
            {main === 'working' && (
              <div className={styles.working}><StreamStatus spinner="searching">The assistant is searching the catalogue…</StreamStatus></div>
            )}
            {/* The prompt sits above the results, not in their place: with Ask phrases the list under
                a question is usually the right one (Shrek for the green ogre). Only the wait after
                asking hides it, so the assistant's own list does not flash in. */}
            <div hidden={main === 'working'}>
              <SearchResults fields={fields} resultsPerPage={6} searchOrAsk={sor}>{renderResult}</SearchResults>
            </div>
          </div>
        </div>
        {/* The column takes the results' height and gives nothing to it: the frame is as tall as
            the results, and the panel fills it, scrolling inside, never stretching it. */}
        {!narrow && <div className={styles.column}><div className={styles.frame}>{side}</div></div>}
      </Base>
      {/* Where a visitor decides what to type: what goes to AI, and the statement that says how. */}
      <p className={demo.credit}>
        Questions are answered by AI from this catalogue.{' '}
        <a href="https://docs.indx.co/gdpr#indxco" target="_blank" rel="noopener noreferrer">How your text is handled</a>.
      </p>
      {/* TMDB's terms ask for this wherever their data or images are shown. */}
      <p className={demo.credit}>
        Film data and posters from <a href="https://www.themoviedb.org" target="_blank" rel="noopener noreferrer">TMDB</a>.
        This product uses TMDB data but is not endorsed or certified by TMDB.
      </p>
    </div>
  );
}

export default function SearchAskDemo(props: Props) {
  return (
    <SearchProvider url={HERO.url} team={HERO.team} dataset={HERO.dataset} preAuthenticatedToken={HERO.token}
                    allowEmptySearch={true} enableFacets={true} maxResults={30} facetDebounceDelayMillis={200}
                    enableDebugLogs={false}>
      <Layout {...props} />
    </SearchProvider>
  );
}
